import type { ReactNode } from "react";
import type { SiteInfo } from "@/components/UI/SitesMap/types";
import { PortfolioMetricTile } from "@/views/Portfolio/shared";
import {
  formatDecimal,
  formatNumberWithGrouping,
} from "@/utils/numberFormatting";

type Props = { siteInfo: SiteInfo | null };

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="text-[10px] font-semibold tracking-widest text-[#556C75] uppercase px-1 pt-2 pb-1">
      {children}
    </div>
  );
}

function MetricsShell({
  columns,
  children,
}: {
  columns: number;
  children: ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-[8px] border border-[#F0F2F4] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
      <div
        className="grid divide-x divide-[#F0F2F4] border-b border-[#F0F2F4]"
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {children}
      </div>
    </div>
  );
}

export default function OverviewTab({ siteInfo }: Props) {
  const s = siteInfo;

  // Enrollment & Progress
  const enrolled = s?.enrolled_count ?? 0;
  const target = s?.subject_count ?? 0;
  const pctToPlan = target > 0 ? Math.round((enrolled / target) * 100) : 0;
  const enrollmentRate = s?.enrollment_rate_derived ?? 0;
  const enrollmentRateUnit = s?.enrollment_rate_unit_derived ?? "sbjs/mo";
  const screenFailurePct = s?.screen_failure_rate_derived ?? 0;
  const monthsOpen = s?.months_open_count_derived ?? 0;

  // Quality & Risk
  const subjectsWithAesPct = s?.subj_with_ae_percent ?? 0;
  const subjectsWithSaesPct = s?.subj_with_sae_percent ?? 0;
  const participantPDs = s?.participant_protocol_deviations_count ?? 0;
  const sitePDs = s?.site_protocol_deviations_count ?? 0;
  const totalPDs = participantPDs + sitePDs;
  const openQueries = s?.open_queries_count_derived ?? 0;
  const discrepancies = s?.data_discrepancies_count ?? 0;

  // Data Completeness
  const sdvCompletePct = s?.sdv_complete_percent_derived ?? 0;
  const missingDataPct = s?.missing_data_percent ?? 0;
  const openMvrItems = s?.open_mvr_action_items_count_derived ?? 0;

  return (
    <div className="flex flex-col p-4">
      <SectionLabel>Enrollment &amp; Progress</SectionLabel>
      <MetricsShell columns={5}>
        <PortfolioMetricTile
          label="% TO PLAN"
          value={formatNumberWithGrouping(pctToPlan)}
          suffix="%"
          progress={pctToPlan}
        />
        <PortfolioMetricTile
          label="ENROLLED / TARGET"
          value={
            <>
              <span>{formatNumberWithGrouping(enrolled)}</span>
              <span className="ml-1 text-[14px] font-normal text-[#2B4752]">
                / {formatNumberWithGrouping(target)} sbjs
              </span>
            </>
          }
          progress={pctToPlan}
        />
        <PortfolioMetricTile
          label="ENROLLMENT RATE"
          value={formatDecimal(enrollmentRate, 2)}
          suffix={enrollmentRateUnit}
        />
        <PortfolioMetricTile
          label="SCREEN FAILURE"
          value={formatDecimal(screenFailurePct, 1)}
          suffix="%"
          progress={screenFailurePct}
          progressTone="gold"
        />
        <PortfolioMetricTile
          label="MONTHS SITE OPEN"
          value={formatDecimal(monthsOpen, 0)}
          suffix="mo"
        />
      </MetricsShell>

      <SectionLabel>Quality &amp; Risk</SectionLabel>
      <MetricsShell columns={5}>
        <PortfolioMetricTile
          label="SUBJECTS WITH AEs"
          value={formatDecimal(subjectsWithAesPct, 0)}
          suffix="%"
          progress={subjectsWithAesPct}
          progressTone="magenta"
        />
        <PortfolioMetricTile
          label="SUBJECTS WITH SAEs"
          value={formatDecimal(subjectsWithSaesPct, 0)}
          suffix="%"
          progress={subjectsWithSaesPct}
          progressTone="magenta"
        />
        <PortfolioMetricTile
          label="PROTOCOL DEVIATIONS"
          value={formatNumberWithGrouping(totalPDs)}
        />
        <PortfolioMetricTile
          label="OPEN QUERIES"
          value={formatNumberWithGrouping(openQueries)}
        />
        <PortfolioMetricTile
          label="DISCREPANCIES"
          value={formatNumberWithGrouping(discrepancies)}
        />
      </MetricsShell>

      <SectionLabel>Data Completeness &amp; Monitoring</SectionLabel>
      <MetricsShell columns={3}>
        <PortfolioMetricTile
          label="SDV COMPLETE"
          value={formatDecimal(sdvCompletePct, 1)}
          suffix="%"
          progress={sdvCompletePct}
        />
        <PortfolioMetricTile
          label="MISSING DATA"
          value={formatDecimal(missingDataPct, 1)}
          suffix="%"
          progress={missingDataPct}
          progressTone="gold"
        />
        <PortfolioMetricTile
          label="OPEN MVR ITEMS"
          value={formatNumberWithGrouping(openMvrItems)}
        />
      </MetricsShell>
    </div>
  );
}
